import { useState } from 'react'
import { useMutation } from '@apollo/client'

import ConfirmReadOnlyBulkActionModal from './ConfirmReadOnlyBulkActionModal'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'
import { RESET_ALL_PLANS_READ_ONLY_MUTATION } from '../../../../graphql/plans'
import {
  ResetAllPlansReadOnlyMutation,
  ResetAllPlansReadOnlyMutationVariables
} from '../../../../generated/graphql'
import {
  READ_ONLY_JOB_STORAGE_KEY,
  READ_ONLY_JOB_STARTED_EVENT,
  BulkReadOnlyJobStartedEventDetail
} from '../../constants/bulkReadOnlyConstants'

type ConfirmReadOnlyBulkActionModalWrapperProps = {
  /** Determines visibility of modal */
  isOpen: boolean
}

/**
 * Wrapper component that handles the bulk read-only mutation.
 * Stores the jobId in localStorage and dispatches an event when the job starts.
 */
const ConfirmReadOnlyBulkActionModalWrapper = ({
  isOpen
}: ConfirmReadOnlyBulkActionModalWrapperProps) => {
  const { hideModal } = useModal()
  const { showSuccessNotif, showErrorNotif } = useNotification()
  const [isLoading, setIsLoading] = useState(false)
  const [readOnlyForStudents, setReadOnlyForStudents] = useState(true)

  const [resetAllPlansReadOnly] = useMutation<
    ResetAllPlansReadOnlyMutation,
    ResetAllPlansReadOnlyMutationVariables
  >(RESET_ALL_PLANS_READ_ONLY_MUTATION, {
    onCompleted: (data) => {
      setIsLoading(false)
      hideModal()

      const jobId = data.resetAllPlansReadOnly?.jobId
      const message =
        data.resetAllPlansReadOnly?.message || 'Read-only bulk action started successfully'

      if (jobId) {
        localStorage.setItem(READ_ONLY_JOB_STORAGE_KEY, jobId)

        window.dispatchEvent(
          new CustomEvent<BulkReadOnlyJobStartedEventDetail>(READ_ONLY_JOB_STARTED_EVENT, {
            detail: { jobId }
          })
        )

        showSuccessNotif('Bulk read-only operation started.')
      } else {
        showSuccessNotif(message)
      }
    },
    onError: (error) => {
      setIsLoading(false)
      hideModal()
      showErrorNotif(`Error starting read-only job: ${error.message}`)
    }
  })

  const onConfirm = () => {
    setIsLoading(true)
    resetAllPlansReadOnly({ variables: { readOnlyForStudents } })
  }

  return (
    <ConfirmReadOnlyBulkActionModal
      isOpen={isOpen && !isLoading}
      onConfirm={onConfirm}
      readOnlyForStudents={readOnlyForStudents}
      onToggleReadOnly={setReadOnlyForStudents}
    />
  )
}

export default ConfirmReadOnlyBulkActionModalWrapper
